import { memo } from 'react';
import { File, FileText, Image, Presentation, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MessageFile } from '@/types/conversation';

interface MessageFilesListProps {
  files: MessageFile[];
  className?: string;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType === 'application/pdf') return FileText;
  if (mimeType.includes('word')) return FileText;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return FileText;
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return Presentation;
  if (mimeType === 'text/csv') return FileText;
  return File;
}

function formatFileSize(bytes: number | string | null): string {
  if (bytes === null || bytes === 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (isNaN(size)) return '';
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function MessageFileItem({ file }: { file: MessageFile }) {
  const IconComponent = getFileIcon(file.mimeType);
  const isImage = file.mimeType.startsWith('image/');

  // For images with download URL, show a thumbnail preview
  if (isImage && file.downloadUrl) {
    return (
      <a
        href={file.downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
        title={file.filename}
      >
        <div className="relative rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors">
          <img
            src={file.downloadUrl}
            alt={file.filename}
            className="max-w-[200px] max-h-[150px] object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </div>
        <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]">
          {file.filename}
        </p>
      </a>
    );
  }

  // For non-image files, show a file card
  return (
    <a
      href={file.downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg',
        'bg-gray-50 border border-gray-200',
        'hover:bg-gray-100 hover:border-gray-300 transition-colors',
        'max-w-[250px]'
      )}
      title={`Scarica ${file.filename}`}
    >
      <IconComponent className="h-5 w-5 text-gray-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 truncate">{file.filename}</p>
        {file.sizeBytes !== null && (
          <p className="text-xs text-gray-400">{formatFileSize(file.sizeBytes)}</p>
        )}
      </div>
      <Download className="h-4 w-4 text-gray-400 shrink-0" />
    </a>
  );
}

function MessageFilesList({ files, className }: MessageFilesListProps) {
  if (!files || files.length === 0) return null;

  const imageFiles = files.filter((f) => f.mimeType.startsWith('image/'));
  const otherFiles = files.filter((f) => !f.mimeType.startsWith('image/'));

  return (
    <div className={cn('space-y-2', className)}>
      {/* Image files in a grid */}
      {imageFiles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {imageFiles.map((file) => (
            <MessageFileItem key={file.id} file={file} />
          ))}
        </div>
      )}

      {/* Other files in a list */}
      {otherFiles.length > 0 && (
        <div className="flex flex-col gap-2">
          {otherFiles.map((file) => (
            <MessageFileItem key={file.id} file={file} />
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(MessageFilesList);
